<template>
  <div>
    <PageHeader :items="items" />
    <validation-observer ref="simpleRules">
      <div class="row">
        <div 
          v-for="template in baseTemplates" 
          :key="template.module"
          class="col-lg-3 col-md-6 col-sm-12 mb-2"
        >
          <b-card class="h-100">
            <b-card-header>
              <h3 class="mb-0">{{ $t(template.label) }}</h3>
            </b-card-header>
            <b-card-body>
              <div class="row mb-0">
                <div class="col-12">
                  <TextInput
                    :label="$t('senderMail')"
                    v-model="getTemplateForLanguage(template.module, 'en').senderMail"
                  />
                </div>
              </div>
              
              <div class="row mb-0">
                <div class="col-12">
                  <TextInput
                    :label="$t('CC')"
                    v-model="getTemplateForLanguage(template.module, 'en').cc"
                  />
                </div>
              </div>
              <div class="row mb-0">
                <div class="col-12">
                  <TextInput
                    :label="$t('BCC')"
                    v-model="getTemplateForLanguage(template.module, 'en').bcc"
                  />
                </div>
              </div>
              <div 
                v-if="['inApprovalReminderTemplate', 'lowCreditsTemplate', 'autoRechargeTemplate'].includes(template.module)"
                class="row mb-0"
              >
                <div class="col-12">
                  <TextInput
                    :label="$t('reminderHours')"
                    type="number"
                    v-model="getTemplateForLanguage(template.module, 'en').reminderHours"
                  />
                </div>
              </div>
              <div class="templates-section">
                <div
                  v-for="language in languages"
                  :key="language.code"
                  class="language-section"
                >
                  <div class="mb-1">
                    <MultiSelectInput
                      v-model="getTemplateForLanguage(template.module, language.code).mailTemplateId"
                      :options="templates"
                      :key="getTemplateForLanguage(template.module, language.code).mailTemplateId"
                      :multiple="false"
                      :textLabel="$t('templateLabel', { language: language.name })"
                      label="subject"
                      trackBy="id"
                      moduleName="mailTemplates"
                      :search-param-name="'search_string'"
                    />
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-end">
        <b-button
          variant="primary"
          @click="createTemplate"
          class="d-flex align-items-center gap-1"
          v-if="$can(`${$route.meta.permission}.save`)"
        >
          <feather-icon icon="PlusIcon" size="12" />
          <span>{{ $t("saveTemplate") }}</span>
        </b-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import PageHeader from "@/components/PageHeader.vue";
import MultiSelect from "vue-multiselect";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapGetters } from "vuex";
import { required, email } from "@validations";
import MultiSelectInput from "@/components/MultiSelectInput.vue";
import mailTemplateAssignmentMixin from "@/mixins/mailTemplateAssignmentMixin";

export default {
  components: {
    TextInput,
    PageHeader,
    MultiSelect,
    ValidationProvider,
    ValidationObserver,
    MultiSelectInput,
  },
  mixins: [mailTemplateAssignmentMixin],
  computed: {
    ...mapGetters("mailTemplates", ["templates"]),
    items() {
      return [
        {
          text: "Dental Twin",
          to: "/home",
        },
        {
          text: this.$t("settings"),
          to: "/settings",
        },
        {
          text: this.$t("mailTemplateAssignment"),
          active: true,
        },
      ];
    },
  },
  data() {
    return {
      baseTemplates: [
        { module: "invoiceTemplate", label: "invoiceTemplate" },
        { module: "invoiceCorrectionTemplate", label: "invoiceCorrectionTemplate" },
        { module: "invoiceStornoTemplate", label: "invoiceStornoTemplate" },
        { module: "userActivateTemplate", label: "userActivateTemplate" },
        { module: "userWelcomeTemplate", label: "userWelcomeTemplate" },
        { module: "ticketSystemTemplate", label: "ticketSystemTemplate" },
        { module: "lowCreditsTemplate", label: "lowCreditsTemplate" },
        { module: "approveCaseTemplate", label: "approveCaseTemplate" },
        { module: "notEnoughCreditsTemplate", label: "notEnoughCreditsTemplate" },
        { module: "inApprovalReminderTemplate", label: "inApprovalReminderTemplate" },
        { module: "autoRechargeTemplate", label: "autoRechargeTemplate" },
        { module: "3dsVerificationTemplate", label: "3dVerificationTemplate" },
        { module: "forgetPasswordTemplate", label: "forgetPasswordTemplate" },  
        { module: "invoiceSummaryTemplate", label: "invoiceSummaryTemplate" },
        { module: "newsletterTemplate", label: "newsletterTemplate" },
      ],
      languageConfigs: [
        { code: "en", name: "English", suffix: "" },
        { code: "de", name: "German", suffix: "De" },
        { code: "fr", name: "French", suffix: "Fr" },
        { code: "tu", name: "Turkish", suffix: "Tu" },
        { code: "es", name: "Spanish", suffix: "Es" },
      ],
      languages: [],
    };
  },
  computed: {
    ...mapGetters("mailTemplates", ["templates"]),
    items() {
      return [
        {
          text: "Dental Twin",
          to: "/home",
        },
        {
          text: this.$t("settings"),
          to: "/settings",
        },
        {
          text: this.$t("mailTemplateAssignment"),
          active: true,
        },
      ];
    },
  },
  created() {
    this.initializeLanguages();
  },
  async mounted() {
    try {
      this.$store.commit("showLoader", true);
      await this.$store.dispatch("mailTemplates/list");
      const response = await this.$store.dispatch(
        "mailTemplates/mailTemplateAssignmentList"
      );

      if (response?.data?.data?.length) {
        const templateData = {};
        
        response.data.data.forEach(item => {
          const langCode = this.getLanguageCodeFromModule(item.module);
          const baseModule = this.getBaseModuleFromFullModule(item.module);
          
          if (!templateData[baseModule]) {
            templateData[baseModule] = {};
          }
          
          templateData[baseModule][langCode] = {
            cc: item.cc || "",
            bcc: item.bcc || "",
            senderMail: item.senderMail || "",
            reminderHours: item.reminderHours || "",
            mailTemplateId: item.mailTemplateId,
            id: item.id
          };
        });

        this.baseTemplates.forEach(template => {
          this.languages.forEach(lang => {
            const templateDataForLang = templateData[template.module]?.[lang.code];
            if (templateDataForLang) {
              const templateObj = this.getTemplateForLanguage(template.module, lang.code);
              this.$set(templateObj, 'cc', templateDataForLang.cc);
              this.$set(templateObj, 'bcc', templateDataForLang.bcc);
              this.$set(templateObj, 'senderMail', templateDataForLang.senderMail);
              this.$set(templateObj, 'reminderHours', templateDataForLang.reminderHours);
              this.$set(templateObj, 'mailTemplateId', templateDataForLang.mailTemplateId);
              this.$set(templateObj, 'id', templateDataForLang.id);
              
              if (templateObj.mailTemplateId) {
                const matchedTemplate = this.templates.find(
                  (t) => t.id === templateObj.mailTemplateId
                );
                if (matchedTemplate) {
                  this.$set(templateObj, 'mailTemplateId', matchedTemplate);
                }
              }
            }
          });
        });
      }
    } catch (e) {
      console.error("api error ___", e);
    } finally {
      this.$store.commit("showLoader", false);
    }
  },
};
</script>

<style scoped lang="scss" src="./Index.scss"></style>
